import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type AbilitySummary } from '@apm/contracts';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AdminMemoryPanel } from '../../features/ai-console/components/admin-memory-panel';
import { HistoryPanel } from '../../features/ai-console/components/history-panel';
import { ModelsPanel } from '../../features/ai-console/components/models-panel';
import { PromptPanel } from '../../features/ai-console/components/prompt-panel';
import { type AiConsoleTabId, aiTabById, visibleAiTabs } from '../../features/ai-console/tabs';
import { AiBudgetPanel } from '../../features/admin/components/ai-budget-panel';
import { KnowledgePanel } from '../../features/admin/components/knowledge-panel';
import { SystemSettingsPanel } from '../../features/admin/components/system-settings-panel';
import { apiFetch } from '../../lib/http';

const Search = z.object({
  /** `?tab=prompts` — id là giao kèo (`AI_CONSOLE_TABS`). Sai thì rơi về tab đầu. */
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute('/_app/system_/ai')({
  component: AiConsolePage,
  validateSearch: Search,
});

/**
 * Bảng điều khiển AI — APM-H-k: một trang, mỗi tab một bảng admin quản lý
 * (kiến thức, ghi nhớ, prompt, chính sách, model, hạn mức). Mỗi bảng đã có API
 * và command ở task gốc; ở đây chỉ gom, không định nghĩa lại. Người thiếu
 * `ai_config:manage` không thấy menu và không thấy tab (API vẫn 403 là cửa thật).
 */
function AiConsolePage(): ReactNode {
  const { t } = useTranslation();
  const { tab: tabId } = Route.useSearch();
  const navigate = Route.useNavigate();
  const abilities = useQuery({
    queryKey: ['abilities'],
    queryFn: () => apiFetch<AbilitySummary>('/me/abilities'),
    staleTime: 5 * 60_000,
  });
  const tabs = visibleAiTabs(abilities.data?.global ?? []);
  const current = tabs.find((tab) => tab.id === aiTabById(tabId)?.id) ?? tabs[0];

  if (abilities.isPending) return null;
  if (!current) {
    return (
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">{t('aiConsole.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aiConsole.forbidden')}</p>
        <Link to="/" className="text-sm underline">
          {t('nav.dashboard')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('aiConsole.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aiConsole.subtitle')}</p>
      </div>

      <Tabs
        value={current.id}
        onValueChange={(value) => void navigate({ search: { tab: value as AiConsoleTabId } })}
      >
        <TabsList aria-label={t('aiConsole.title')}>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {t(tab.titleKey)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <section className="flex flex-col gap-3" aria-label={t(current.titleKey)}>
        {current.aggregate ? <HistoryPanel aggregateType={current.aggregate} /> : null}
        <TabBody tab={current.id} />
      </section>
    </div>
  );
}

function TabBody({ tab }: { tab: AiConsoleTabId }): ReactNode {
  switch (tab) {
    case 'knowledge':
      return <KnowledgePanel />;
    case 'memory':
      return <AdminMemoryPanel />;
    case 'prompts':
      return <PromptPanel />;
    case 'policy':
      // Form sinh từ `SYSTEM_SETTINGS` (Zod) — thêm khoá ở backend là form có trường mới; lọc nhóm `ai_policy`.
      return <SystemSettingsPanel groups={['ai_policy']} />;
    case 'models':
      return <ModelsPanel />;
    case 'budgets':
      return <AiBudgetPanel />;
  }
}
